import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import InputBar from "./chat/InputBar";
import type { Attachment, InferenceStatus } from "./chat/types";

export interface WorkspaceComposerFeedback {
  message: string;
  tone: "success" | "error" | "info";
}

interface WorkspaceComposerProps {
  surface: string;
  context: Record<string, unknown>;
  feedback?: WorkspaceComposerFeedback | null;
  onSend: (text: string, context: Record<string, unknown>) => void;
}

async function fetchInferenceStatus(): Promise<InferenceStatus> {
  const res = await fetch("/api/inference/status");
  if (!res.ok) {
    throw new Error("Failed to fetch inference status");
  }
  return res.json();
}

function buildPlaceholder(surface: string): string {
  switch (surface) {
    case "life":
      return "Ask your agent about Life...";
    case "projects":
      return "Ask your agent about Projects...";
    case "workstreams":
      return "Ask your agent about Workstreams...";
    case "files":
      return "Ask your agent about Files...";
    case "settings":
      return "Ask your agent to change a setting...";
    case "apps":
      return "Ask your agent about this app...";
    default:
      return "Message your agent...";
  }
}

export default function WorkspaceComposer({
  surface,
  context,
  feedback,
  onSend,
}: WorkspaceComposerProps): React.ReactElement {
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const inferenceStatusQ = useQuery({
    queryKey: ["workspace-composer", "inference-status"],
    queryFn: fetchInferenceStatus,
  });

  const placeholder = useMemo(() => buildPlaceholder(surface), [surface]);

  return (
    <div
      style={{
        filter: "drop-shadow(0 22px 42px rgba(15, 23, 42, 0.16))",
      }}
    >
      {feedback && (
        <div
          role="status"
          aria-live="polite"
          data-workspace-composer-feedback={feedback.tone}
          className="max-w-3xl mx-auto mb-2 flex items-start gap-2 rounded-lg px-3 py-2 text-xs"
          style={{
            color:
              feedback.tone === "error"
                ? "var(--c-danger)"
                : feedback.tone === "success"
                  ? "var(--c-success)"
                  : "var(--c-text)",
            backgroundColor: "var(--c-surface)",
            border: `1px solid ${
              feedback.tone === "error"
                ? "var(--c-danger)"
                : feedback.tone === "success"
                  ? "var(--c-success)"
                  : "var(--c-border-hover)"
            }`,
          }}
        >
          <span
            aria-hidden="true"
            className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full"
            style={{
              backgroundColor:
                feedback.tone === "error"
                  ? "var(--c-danger)"
                  : feedback.tone === "success"
                    ? "var(--c-success)"
                    : "var(--c-primary)",
            }}
          />
          <span className="min-w-0 leading-relaxed">{feedback.message}</span>
        </div>
      )}
      <InputBar
        input={input}
        onInputChange={setInput}
        onSend={() => {
          const trimmed = input.trim();
          if (!trimmed) return;
          onSend(trimmed, context);
          setInput("");
          setAttachments([]);
        }}
        onStop={() => {}}
        streaming={false}
        model="auto"
        onModelChange={() => {}}
        availableModels={[]}
        sessionTotalTokens={0}
        sessionTotalCost={0}
        attachments={attachments}
        onAttachmentsChange={setAttachments}
        inferenceStatus={inferenceStatusQ.data}
        placeholder={placeholder}
        allowAttachments={false}
        showKeyboardHint={false}
      />
    </div>
  );
}
